import React, { useEffect, useRef, useState } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { X, MapPin } from 'lucide-react';
import './LocationPickerModal.css';
import {
  getAllNeighborhoods,
  bogotaLocalities,
  getLocalityFromNeighborhood,
  normalizeString,
  findNearestNeighborhood
} from '../data/bogota-neighborhoods';
import { DEFAULT_MAP_INITIAL_VIEW, getMapInitialView } from '../utils/mapInitialView';

interface LocationPickerModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSelectLocation: (lat: number, lng: number, neighborhood?: string, location?: string) => void;
  initialLat?: number;
  initialLng?: number;
}

const LocationPickerModal: React.FC<LocationPickerModalProps> = ({
  isOpen,
  onClose,
  onSelectLocation,
  initialLat,
  initialLng,
}) => {
  const mapRef = useRef<L.Map | null>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const isInitializedRef = useRef(false);
  
  const [selectedCoords, setSelectedCoords] = useState<{ lat: number; lng: number } | null>(null);
  const [detectedNeighborhood, setDetectedNeighborhood] = useState<string>('');
  const [detectedLocation, setDetectedLocation] = useState<string>('');
  const [isDetecting, setIsDetecting] = useState(false);

  // Función para detectar localidad y barrio desde OpenStreetMap
  const detectLocalityAndNeighborhood = async (lat: number, lng: number) => {
    setIsDetecting(true);
    try {
      const response = await fetch(
        `https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lng}&format=json`
      );
      const data = await response.json();
      const address = data.address || {};
      
      console.log('📍 DATOS OSM:', data);
      console.log('📍 Address:', address);
      
      const allNeighborhoods = getAllNeighborhoods();
      let matchedLocalidad = '';
      let matchedBarrio = '';
      
      // PASO 1: Detectar LOCALIDAD usando campos específicos de OSM
      const localidadCandidates = [
        address.city_district,
        address.borough,
        address.county,
        address.state_district,
        address.suburb?.includes('Localidad') ? address.suburb : null,
      ].filter(Boolean) as string[];
      
      for (const candidato of localidadCandidates) {
        const candidatoNormalized = normalizeString(candidato);
        
        const found = bogotaLocalities.find(loc => {
          const locNormalized = normalizeString(loc);
          if (locNormalized === candidatoNormalized) return true;
          if (candidatoNormalized.includes(locNormalized) || locNormalized.includes(candidatoNormalized)) return true;
          if (candidatoNormalized.includes('localidad')) {
            const cleaned = candidatoNormalized.replace('localidad', '').trim();
            if (cleaned === locNormalized || cleaned.includes(locNormalized) || locNormalized.includes(cleaned)) return true;
          }
          return false;
        });
        if (found) {
          matchedLocalidad = found;
          break;
        }
      }
      
      // PASO 2: Detectar BARRIO usando campos específicos de OSM
      const barrioCandidates = [
        address.neighbourhood,
        address.quarter,
        !address.suburb?.includes('Localidad') ? address.suburb : null,
        address.hamlet,
        address.village,
        address.residential,
      ].filter(Boolean) as string[];
      
      for (const candidato of barrioCandidates) {
        const candidatoNormalized = normalizeString(candidato);
        
        // Ignorar si es una localidad conocida
        const isLocalidad = bogotaLocalities.some(loc => normalizeString(loc) === candidatoNormalized);
        if (isLocalidad) {
          continue;
        }
        
        const found = allNeighborhoods.find(b => {
          const bNormalized = normalizeString(b);
          if (bNormalized === candidatoNormalized || candidatoNormalized.includes(bNormalized) || bNormalized.includes(candidatoNormalized)) {
            // VALIDAR que el barrio pertenezca a la localidad detectada
            if (matchedLocalidad) {
              const barrioLocalidad = getLocalityFromNeighborhood(b);
              if (barrioLocalidad !== matchedLocalidad) {
                return false;
              }
            }
            return true;
          }
          return false;
        });
        if (found) {
          matchedBarrio = found;
          break;
        }
      }
      
      // PASO 3: Si NO se encontró barrio en OSM, buscar el BARRIO MÁS CERCANO por coordenadas
      if (!matchedBarrio && matchedLocalidad) {
        const nearestBarrio = findNearestNeighborhood(lat, lng, matchedLocalidad);
        if (nearestBarrio) {
          matchedBarrio = nearestBarrio;
        }
      }
      
      // PASO 4: Si se encontró barrio pero no localidad, obtener localidad del barrio
      if (matchedBarrio && !matchedLocalidad) {
        matchedLocalidad = getLocalityFromNeighborhood(matchedBarrio) || '';
      }
      
      console.log('📊 RESULTADO FINAL:');
      console.log('  - Localidad:', matchedLocalidad || 'NO DETECTADA');
      console.log('  - Barrio:', matchedBarrio || 'NO DETECTADO');
      
      setDetectedLocation(matchedLocalidad);
      setDetectedNeighborhood(matchedBarrio);
    } catch (error) {
      console.error('Error en geocodificación inversa:', error);
      setDetectedLocation('');
      setDetectedNeighborhood('');
    } finally {
      setIsDetecting(false);
    }
  };

  useEffect(() => {
    if (isOpen && mapContainerRef.current && !mapRef.current) {
      // Vista inicial: si el llamador pasó lat/lng las usa; si no, cae al default y luego
      // se ajusta con lo que devuelva el backend (MAP_INITIAL_LAT/LNG).
      const startLat = initialLat ?? DEFAULT_MAP_INITIAL_VIEW.lat;
      const startLng = initialLng ?? DEFAULT_MAP_INITIAL_VIEW.lng;

      // Crear mapa solo la primera vez
      const map = L.map(mapContainerRef.current).setView([startLat, startLng], 13);

      // Agregar tiles
      L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '© OpenStreetMap contributors'
      }).addTo(map);

      // Icono personalizado
      const customIcon = L.divIcon({
        className: 'custom-marker',
        html: `<div style="background-color: #A61B34; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
        iconSize: [30, 30],
        iconAnchor: [15, 15],
      });

      // Si el llamador pasó coordenadas iniciales explícitas, agregar marker
      if (initialLat != null && initialLng != null) {
        markerRef.current = L.marker([initialLat, initialLng], { icon: customIcon }).addTo(map);
      } else {
        // Sin coordenadas explícitas: ajustar centro con la config del backend
        getMapInitialView().then((view) => {
          if (!mapRef.current) return;
          mapRef.current.setView([view.lat, view.lng], 13);
        }).catch(() => { /* fallback ya aplicado */ });
      }

      // Evento de clic en el mapa
      map.on('click', async (e: L.LeafletMouseEvent) => {
        const { lat, lng } = e.latlng;

        const icon = L.divIcon({
          className: 'custom-marker',
          html: `<div style="background-color: #A61B34; width: 30px; height: 30px; border-radius: 50%; border: 3px solid white; box-shadow: 0 2px 8px rgba(0,0,0,0.3);"></div>`,
          iconSize: [30, 30],
          iconAnchor: [15, 15],
        });

        // Actualizar o crear marker
        if (markerRef.current) {
          markerRef.current.setLatLng([lat, lng]);
        } else {
          markerRef.current = L.marker([lat, lng], { icon }).addTo(map);
        }

        // Guardar coordenadas
        setSelectedCoords({ lat, lng });
        
        // Detectar localidad y barrio automáticamente
        await detectLocalityAndNeighborhood(lat, lng);
      });

      mapRef.current = map;
      isInitializedRef.current = true;

      // Fix para que el mapa se renderice correctamente
      setTimeout(() => {
        map.invalidateSize();
      }, 100);
    }

    // Cleanup cuando se cierra el modal completamente
    if (!isOpen && mapRef.current) {
      mapRef.current.remove();
      mapRef.current = null;
      markerRef.current = null;
      isInitializedRef.current = false;
    }
  }, [isOpen]);

  const handleConfirm = () => {
    if (selectedCoords) {
      onSelectLocation(
        selectedCoords.lat, 
        selectedCoords.lng,
        detectedNeighborhood,
        detectedLocation
      );
    }
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="location-picker-overlay">
      <div className="location-picker-container">
        {/* Header */}
        <div className="location-picker-header">
          <div>
            <h3 className="location-picker-title">Seleccionar Ubicación</h3>
            <p className="location-picker-subtitle">Haga clic en el mapa para seleccionar las coordenadas</p>
          </div>
          <button onClick={onClose} className="location-picker-close-btn">
            <X size={24} />
          </button>
        </div>

        {/* Map */}
        <div className="location-picker-map-container">
          <div ref={mapContainerRef} className="location-picker-map" />
        </div>

        {/* Location Info */}
        {selectedCoords && (
          <div className="location-picker-info">
            {isDetecting ? (
              <div className="location-picker-detecting">
                <MapPin size={16} className="animate-pulse" />
                <span>Detectando localidad y barrio...</span>
              </div>
            ) : (
              <div className="location-picker-details">
                <div className="location-picker-detail-item">
                  <span className="location-picker-detail-label">Coordenadas:</span>
                  <span className="location-picker-detail-value">
                    {selectedCoords.lat.toFixed(6)}, {selectedCoords.lng.toFixed(6)}
                  </span>
                </div>
                {detectedLocation && (
                  <div className="location-picker-detail-item">
                    <span className="location-picker-detail-label">Localidad:</span>
                    <span className="location-picker-detail-value">{detectedLocation}</span>
                  </div>
                )}
                {detectedNeighborhood && (
                  <div className="location-picker-detail-item">
                    <span className="location-picker-detail-label">Barrio:</span>
                    <span className="location-picker-detail-value">{detectedNeighborhood}</span>
                  </div>
                )}
                {!detectedLocation && !detectedNeighborhood && !isDetecting && (
                  <div className="location-picker-detail-item">
                    <span className="location-picker-detail-value" style={{ color: '#9ca3af' }}>
                      No se pudo detectar localidad o barrio
                    </span>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Footer */}
        <div className="location-picker-footer">
          <button onClick={onClose} className="location-picker-btn-secondary">
            Cancelar
          </button>
          <button 
            onClick={handleConfirm} 
            className="location-picker-btn-primary"
            disabled={!selectedCoords}
          >
            Confirmar
          </button>
        </div>
      </div>
    </div>
  );
};

export default LocationPickerModal;
